import { useEffect, useState } from 'react';
import './styles.css';

function Icon({ name, size = 20 }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (name === 'search') return <svg {...common}><circle cx="10.8" cy="10.8" r="6.8"/><path d="m16 16 4.3 4.3"/></svg>;
  if (name === 'box') return <svg {...common}><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z"/><path d="m4.3 7.6 7.7 4.5 7.7-4.5M12 12v9M8 5.3l8 4.6"/></svg>;
  if (name === 'truck') return <svg {...common}><path d="M3 6h11v11H3zM14 10h4l3 3v4h-7z"/><circle cx="7.5" cy="18" r="1.7"/><circle cx="18" cy="18" r="1.7"/></svg>;
  if (name === 'arrow') return <svg {...common}><path d="M5 12h14M13 6l6 6-6 6"/></svg>;
  if (name === 'external') return <svg {...common}><path d="M14 4h6v6M20 4l-9 9"/><path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6"/></svg>;
  return <svg {...common}><path d="M12 8v4l2.5 1.5"/><circle cx="12" cy="12" r="9"/></svg>;
}

function carrierUrl(carrier, tracking) {
  const value = encodeURIComponent(tracking);
  const name = carrier.toLowerCase();
  if (name.includes('ups')) return `https://www.ups.com/track?tracknum=${value}`;
  if (name.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${value}`;
  if (name.includes('usps')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${value}`;
  return `https://www.google.com/search?q=${encodeURIComponent(`${carrier} tracking ${tracking}`)}`;
}

function App({ apiBaseUrl = '/api/sanmar-tracking-lookup' }) {
  useEffect(() => {
    document.body.classList.add('sanmar-tracking-layout');
    return () => document.body.classList.remove('sanmar-tracking-layout');
  }, []);
  const [poNumber, setPoNumber] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [configured, setConfigured] = useState(null);

  useEffect(() => {
    fetch(`${apiBaseUrl}/health`)
      .then((response) => response.json())
      .then((data) => setConfigured(data.configured))
      .catch(() => setConfigured(false));
  }, [apiBaseUrl]);

  async function lookup(event) {
    event.preventDefault();
    const value = poNumber.trim();
    if (!value) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const response = await fetch(`${apiBaseUrl}/shipments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ poNumber: value }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'The lookup could not be completed.');
      setResult(data);
    } catch (err) {
      setError(err.message || 'Unable to connect to the lookup service.');
    } finally {
      setLoading(false);
    }
  }

  const records = result?.records || [];
  const packages = [...new Map(records.filter((row) => row.trackingNumber).map((row) => [row.trackingNumber, row])).values()];

  return (
    <main className="sanmar-tracking"><div className="page-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="SanMar Tracking Lookup home">
          <span className="brand-mark"><Icon name="box" size={21} /></span>
          <span className="brand-name">shipment<span>desk</span></span>
        </a>
        <div className="topbar-right"><span className="live-dot" /> SANMAR CONNECTION <span className="topbar-divider">/</span> {configured === null ? 'CHECKING' : configured ? 'READY' : 'SETUP NEEDED'}</div>
      </header>

      <section className="hero" id="top">
        <div className="hero-copy">
          <div className="eyebrow"><span className="eyebrow-line" /> SHIPPING OPERATIONS</div>
          <h1>Every shipment.<br /><em>One clear view.</em></h1>
          <p>Find SanMar package details and tracking numbers with a purchase order number.</p>
        </div>
        <div className="hero-stamp" aria-hidden="true"><Icon name="truck" size={35} /><span>TRACK<br />WITH EASE</span></div>
      </section>

      <section className="lookup-card" aria-label="Shipment lookup">
        <div className="lookup-card-heading"><div><div className="section-kicker">ORDER LOOKUP</div><h2>Where is your order headed?</h2></div><span className="step-count">01 <i>/</i> 01</span></div>
        <form className="lookup-form" onSubmit={lookup}>
          <label htmlFor="po-input">PURCHASE ORDER NUMBER</label>
          <div className="input-row">
            <div className="input-wrap"><Icon name="search" size={19} /><input id="po-input" autoComplete="off" placeholder="e.g. PO12345" value={poNumber} onChange={(event) => setPoNumber(event.target.value)} /></div>
            <button type="submit" disabled={loading || !poNumber.trim()}>{loading ? <><span className="spinner" /> Searching</> : <>Find shipments <Icon name="arrow" size={18} /></>}</button>
          </div>
          <div className="form-foot"><span><Icon name="clock" size={14} /> Usually takes a few seconds</span><span>LOOKUP BY SANMAR PO</span></div>
        </form>
      </section>

      {error && <div className="notice error-notice" role="alert"><span className="notice-icon">!</span><div><strong>We couldn’t complete that lookup</strong><p>{error}</p></div></div>}
      {configured === false && !error && <div className="notice setup-notice"><span className="notice-icon">i</span><div><strong>Server setup needed</strong><p>SanMar credentials are not configured on the server.</p></div></div>}

      {result && <section className="results-section" aria-live="polite">
        <div className="results-heading"><div><div className="section-kicker">SHIPMENT RESULTS</div><h2>Order <span>{result.poNumber}</span></h2></div><span className="result-count">{records.length} {records.length === 1 ? 'LINE' : 'LINES'}</span></div>
        {records.length === 0 ? <div className="empty-state"><div className="empty-icon"><Icon name="box" size={25} /></div><h3>No shipment details yet</h3><p>SanMar returned no shipment records for this PO. Try again later if the order has not shipped.</p></div> : <>
          <div className="summary-strip"><div className="summary-icon"><Icon name="truck" size={20} /></div><div><strong>{packages.length} {packages.length === 1 ? 'package' : 'packages'} with tracking</strong><span>{records.length} item {records.length === 1 ? 'line' : 'lines'} across this order</span></div><span className="summary-check">✓</span></div>
          <div className="shipment-list">{packages.length ? packages.map((pkg) => {
            const packageRows = records.filter((row) => row.trackingNumber === pkg.trackingNumber);
            return <article className="shipment-card" key={pkg.trackingNumber}>
              <div className="shipment-top"><div className="shipment-icon"><Icon name="box" size={19} /></div><div className="shipment-meta"><span className="shipment-label">PACKAGE TRACKING</span><strong>{pkg.carrier || 'Carrier'} <span className="ship-date">{pkg.shipDate ? `· Shipped ${pkg.shipDate}` : ''}</span></strong></div><a className="track-link" href={carrierUrl(pkg.carrier, pkg.trackingNumber)} target="_blank" rel="noreferrer">Track package <Icon name="external" size={15} /></a></div>
              <div className="tracking-number"><span>{pkg.trackingNumber}</span><span className="tracking-caption">TRACKING NUMBER</span></div>
              <div className="items-head"><span>ITEM</span><span>SALES ORDER</span><span>QTY SHIPPED</span></div>
              {packageRows.map((row, index) => <div className="item-row" key={`${row.sku}-${index}`}><span className="sku"><span className="sku-dot" />{row.sku}</span><span>{row.salesOrder || '—'}</span><span>{row.quantity}</span></div>)}
            </article>;
          }) : <div className="empty-state"><h3>Shipment found, tracking not assigned</h3><p>SanMar returned {records.length} item {records.length === 1 ? 'line' : 'lines'}, but no tracking number is available yet.</p></div>}</div>
        </>}
      </section>}

      {!result && !error && <section className="how-section"><div className="how-label">A QUICK CHECK-IN</div><div className="how-item"><span className="how-number">01</span><div><strong>Enter a PO number</strong><p>Use the purchase order submitted to SanMar.</p></div></div><div className="how-item"><span className="how-number">02</span><div><strong>Get shipment details</strong><p>See package tracking, ship dates, and items.</p></div></div><div className="how-item"><span className="how-number">03</span><div><strong>Open carrier tracking</strong><p>Jump directly to the carrier’s tracking page.</p></div></div></section>}

      <footer className="footer"><span>SHIPMENT DESK <span className="footer-dot">·</span> SANMAR ORDER LOOKUP</span><span>Shipment details provided by SanMar</span></footer>
    </div></main>
  );
}

export default App;
